import React, { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, deleteUser, resendUserInvite, getInviteLink, getAllXeroContacts, getXeroStatus, mapXeroContact, sendUserPasswordReset, forceSignOutUser, setUserDisabled } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Plus, Mail, Phone, Pencil, Trash2, Building2, BriefcaseBusiness, Link2, AlertTriangle, Copy, Send, KeyRound, LogOut, Ban, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_COLORS = {
  admin: 'bg-blue-100 text-blue-800 border-blue-200',
  employee: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  subcontractor: 'bg-amber-100 text-amber-800 border-amber-200',
  customer: 'bg-slate-100 text-slate-800 border-slate-200',
};

const emptyForm = {
  email: '', password: '', name: '',
  role: 'customer', phone: '', title: '', company: '',
  xeroContactId: '',
};

const Users = () => {
  const { user: me } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filter, setFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [xeroConnected, setXeroConnected] = useState(false);
  const [xeroContacts, setXeroContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Quick "Link to Xero" dialog (remap without editing full user)
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUser, setLinkUser] = useState(null);
  const [linkQuery, setLinkQuery] = useState('');
  const [linkSaving, setLinkSaving] = useState(false);

  const openLink = (u) => {
    setLinkUser(u);
    setLinkQuery('');
    setLinkOpen(true);
  };

  const saveLink = async (xeroContactId) => {
    if (!linkUser) return;
    setLinkSaving(true);
    try {
      await mapXeroContact(linkUser.id, xeroContactId || '');
      toast.success(xeroContactId ? 'Linked to Xero contact' : 'Xero link cleared');
      setLinkOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Link failed');
    } finally {
      setLinkSaving(false);
    }
  };

  const refresh = () => getUsers().then(setList);
  useEffect(() => {
    refresh().finally(() => setLoading(false));
    getXeroStatus().then(s => setXeroConnected(!!s?.connected)).catch(() => {});
  }, []);

  // Load Xero contacts once Xero status is known and user is connected.
  // This runs once and caches the result for every future Invite/Edit dialog open.
  useEffect(() => {
    if (!xeroConnected || xeroContacts.length > 0 || loadingContacts) return;
    setLoadingContacts(true);
    getAllXeroContacts()
      .then((contacts) => {
        setXeroContacts(contacts || []);
        if (!contacts || contacts.length === 0) {
          toast.error('Xero returned 0 contacts. Check your Xero connection.');
        }
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || 'Failed to load Xero contacts');
      })
      .finally(() => setLoadingContacts(false));
  }, [xeroConnected]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email || '', password: '', name: u.name || '',
      role: u.role || 'customer', phone: u.phone || '',
      title: u.title || '', company: u.company || '',
      xeroContactId: u.xeroContactId || '',
    });
    setOpen(true);
  };

  const save = async () => {
    // For customer records we identify by company (we only work for
    // businesses). For admin/employee records we still need a person's name.
    const isCustomer = form.role === 'customer';
    if (isCustomer) {
      if (!form.company.trim()) {
        return toast.error('Company name is required');
      }
    } else {
      if (!form.name.trim() || !form.email.trim()) {
        return toast.error('Name and email are required');
      }
    }
    setSaving(true);
    try {
      // Keep `name` populated so downstream code (invite emails, cards,
      // queries) that reads user.name keeps working — customers get their
      // company name as the canonical identifier.
      const normalised = isCustomer
        ? { ...form, name: form.company.trim() }
        : { ...form };
      if (editing) {
        const payload = { ...normalised };
        if (!payload.password) delete payload.password;
        await updateUser(editing.id, payload);
        toast.success('User updated');
      } else {
        const payload = { ...normalised };
        if (!payload.password) delete payload.password;
        const res = await createUser(payload);
        // If the admin chose to invite (no password) and the email failed,
        // copy the invite link to the clipboard and tell them to share it
        // manually. Far better than a silent dead-end.
        if (!payload.password && res?.inviteEmailSent === false && res?.inviteUrl) {
          try { await navigator.clipboard.writeText(res.inviteUrl); } catch { /* clipboard may be denied */ }
          toast.error(
            'Invite created, but the email couldn\'t be delivered. We copied the invite link to your clipboard — paste it to the user yourself.',
            { duration: 12000 },
          );
        } else if (!payload.password && !payload.email) {
          toast.success('Customer created (no company email — add people under Business employees & contacts)');
        } else if (!payload.password) {
          toast.success(`${form.role.charAt(0).toUpperCase() + form.role.slice(1)} invited · email sent to ${form.email}`);
        } else {
          toast.success('User created');
        }
      }
      setOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleResendInvite = async (u) => {
    try {
      const res = await resendUserInvite(u.id);
      if (res?.inviteEmailSent === false && res?.inviteUrl) {
        try { await navigator.clipboard.writeText(res.inviteUrl); } catch { /* clipboard may be denied */ }
        toast.error('Email failed — invite link copied to clipboard.', { duration: 10000 });
      } else {
        toast.success(`Invite resent to ${u.email}`);
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    }
  };

  const handleCopyInviteLink = async (u) => {
    try {
      const { inviteUrl } = await getInviteLink(u.id);
      await navigator.clipboard.writeText(inviteUrl);
      toast.success('Invite link copied to clipboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate link');
    }
  };

  const handleSendPasswordReset = async (u) => {
    // Used when a user says "I can't log in" — we email them a fresh
    // reset link *and* copy it to the admin's clipboard so support can
    // paste it into whatever channel the user is reachable on (SMS,
    // Slack, WhatsApp) when the email path is blocked.
    if (!u.email) return toast.error('This user has no email on file');
    try {
      const res = await sendUserPasswordReset(u.id);
      if (res?.resetUrl) {
        try { await navigator.clipboard.writeText(res.resetUrl); } catch { /* ignore */ }
      }
      toast.success(`Password-reset email sent to ${u.email} — link also copied to your clipboard.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send password reset');
    }
  };

  const handleForceSignOut = async (u) => {
    if (!window.confirm(`Sign ${u.name} out of every device?`)) return;
    try {
      const { revoked } = await forceSignOutUser(u.id);
      toast.success(revoked ? `${u.name} signed out of ${revoked} session${revoked === 1 ? '' : 's'}` : `${u.name} had no active sessions`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not sign user out');
    }
  };

  const handleToggleDisabled = async (u) => {
    const disabling = !u.disabled;
    if (disabling && !window.confirm(`Disable ${u.name}? They'll be signed out everywhere and blocked from signing in.`)) return;
    try {
      await setUserDisabled(u.id, disabling);
      toast.success(disabling ? `${u.name} disabled and signed out` : `${u.name} re-enabled`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update account status');
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}? They'll lose portal access immediately.`)) return;
    try {
      await deleteUser(u.id);
      toast.success('User deleted');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = filter === 'all' ? list : list.filter(u => u.role === filter);
  const counts = {
    all: list.length,
    admin: list.filter(u => u.role === 'admin').length,
    employee: list.filter(u => u.role === 'employee').length,
    subcontractor: list.filter(u => u.role === 'subcontractor').length,
    customer: list.filter(u => u.role === 'customer').length,
  };

  if (loading) return <div className="p-8 text-slate-500">Loading users...</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Users"
        description="Manage admins, employees and customers who have access to the portal."
        actions={
          <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800" data-testid="invite-user-btn">
            <Plus className="h-4 w-4 mr-2" /> Invite User
          </Button>
        }
      />

      {/* Filter tabs */}
      <div className="flex items-center gap-2 text-sm">
        {[
          { key: 'all', label: 'All' },
          { key: 'admin', label: 'Admins' },
          { key: 'employee', label: 'Employees' },
          { key: 'subcontractor', label: 'Subcontractors' },
          { key: 'customer', label: 'Customers' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            data-testid={`filter-${t.key}`}
            className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
              filter === t.key
                ? 'bg-slate-900 text-white'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            {t.label} <span className="text-xs opacity-70">({counts[t.key]})</span>
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(u => {
          const isSelf = me?.id === u.id;
          return (
            <div
              key={u.id}
              data-testid={`user-card-${u.id}`}
              className={`bg-white border border-slate-200 rounded-md p-5 group hover:border-slate-400 transition-colors min-w-0 overflow-hidden ${u.role === 'employee' ? 'cursor-pointer' : ''}`}
              onClick={(e) => {
                // Engineer cards are click-to-profile. Stop here only if the
                // click came from a button — admins still want pencil/trash
                // actions on top of the card.
                if (u.role !== 'employee') return;
                if (e.target.closest('button')) return;
                window.location.href = `/admin/users/${u.id}`;
              }}
            >
              <div className="flex items-start justify-between gap-3 mb-3 min-w-0">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Avatar className="h-12 w-12 shrink-0">
                    <AvatarFallback className="bg-slate-900 text-white font-semibold">
                      {u.avatar || u.name.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                      {u.name}
                      {isSelf && <span className="text-xs font-normal text-slate-500">(you)</span>}
                    </div>
                    <span className={`inline-block mt-0.5 text-xs font-medium border px-2 py-0.5 rounded capitalize ${ROLE_COLORS[u.role]}`}>{u.role}</span>
                    {u.disabled && (
                      <span className="inline-block mt-0.5 ml-1.5 text-xs font-medium border px-2 py-0.5 rounded bg-red-50 text-red-700 border-red-200" data-testid={`user-disabled-badge-${u.id}`}>Disabled</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {!isSelf && (
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  )}
                </div>
              </div>
              {u.title && <div className="text-xs text-slate-500 flex items-center gap-1.5 mb-1"><BriefcaseBusiness className="h-3 w-3" /> {u.title}</div>}
              {u.company && <div className="text-xs text-slate-500 flex items-center gap-1.5 mb-2"><Building2 className="h-3 w-3" /> {u.company}</div>}
              {u.role === 'customer' && xeroConnected && (
                <div className="mb-2">
                  {u.xeroContactId ? (
                    <button
                      type="button"
                      onClick={() => openLink(u)}
                      data-testid={`remap-xero-${u.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 px-2 py-0.5 rounded transition-colors"
                      title="Change the Xero contact this customer is linked to"
                    >
                      <Link2 className="h-3 w-3" /> Linked to Xero
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openLink(u)}
                      data-testid={`link-xero-${u.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 px-2 py-0.5 rounded transition-colors"
                      title="Link this customer to an existing Xero contact"
                    >
                      <Link2 className="h-3 w-3" /> Link to Xero
                    </button>
                  )}
                </div>
              )}
              <div className="space-y-1.5 text-sm pt-3 border-t border-slate-100 min-w-0">
                <div className="flex items-center gap-2 text-slate-600 min-w-0">
                  <Mail className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate min-w-0 flex-1">{u.email || <span className="text-slate-400 italic">no email</span>}</span>
                </div>
                {u.phone && <div className="flex items-center gap-2 text-slate-600 min-w-0"><Phone className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{u.phone}</span></div>}
              </div>
              {!isSelf && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex gap-1.5" data-testid={`user-access-${u.id}`}>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs flex-1"
                    onClick={(e) => { e.stopPropagation(); handleForceSignOut(u); }}
                    data-testid={`force-signout-${u.id}`}
                  >
                    <LogOut className="h-3 w-3 mr-1" /> Force sign-out
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className={`h-7 text-xs flex-1 ${u.disabled ? 'text-emerald-700 border-emerald-200 hover:bg-emerald-50' : 'text-red-600 border-red-200 hover:bg-red-50'}`}
                    onClick={(e) => { e.stopPropagation(); handleToggleDisabled(u); }}
                    data-testid={`toggle-disabled-${u.id}`}
                  >
                    {u.disabled ? <><CheckCircle2 className="h-3 w-3 mr-1" /> Enable account</> : <><Ban className="h-3 w-3 mr-1" /> Disable account</>}
                  </Button>
                </div>
              )}
              {!isSelf && u.inviteStatus === 'pending' && (
                <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`invite-pending-${u.id}`}>
                  <div className="flex items-center gap-1.5 text-xs text-amber-700 mb-2">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    <span>Invite not yet accepted</span>
                  </div>
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs flex-1"
                      onClick={(e) => { e.stopPropagation(); handleCopyInviteLink(u); }}
                      data-testid={`copy-invite-${u.id}`}
                    >
                      <Copy className="h-3 w-3 mr-1" /> Copy link
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs flex-1"
                      onClick={(e) => { e.stopPropagation(); handleResendInvite(u); }}
                      data-testid={`resend-invite-${u.id}`}
                    >
                      <Send className="h-3 w-3 mr-1" /> Resend email
                    </Button>
                  </div>
                </div>
              )}
              {!isSelf && u.inviteStatus !== 'pending' && u.email && (
                <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`user-reset-${u.id}`}>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs w-full"
                    onClick={(e) => { e.stopPropagation(); handleSendPasswordReset(u); }}
                    data-testid={`send-reset-${u.id}`}
                    title="Email this user a link to choose a new password"
                  >
                    <KeyRound className="h-3 w-3 mr-1" /> Send password reset
                  </Button>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full p-12 text-center text-slate-500 bg-white border border-dashed border-slate-200 rounded-md">
            No users match this filter.
          </div>
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'Invite new user'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Update the user\'s details. Leave password blank to keep the existing one.'
                : 'Creates the account and emails an invite link. Leave password blank — the user picks their own.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {form.role !== 'customer' ? (
                <div>
                  <Label>Full name *</Label>
                  <Input data-testid="user-name-input" className="mt-1.5" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Sarah Mitchell" />
                </div>
              ) : (
                <div>
                  <Label>Company *</Label>
                  <XeroContactPicker
                    xeroConnected={xeroConnected}
                    loading={loadingContacts}
                    contacts={xeroContacts}
                    value={form.company}
                    xeroContactId={form.xeroContactId}
                    onSelect={(c) => setForm({
                      ...form,
                      company: c ? c.name : form.company,
                      xeroContactId: c ? c.xeroContactId : '',
                      email: form.email || (c?.email || ''),
                      phone: form.phone || (c?.phone || ''),
                    })}
                    onTypeCustom={(txt) => setForm({ ...form, company: txt, xeroContactId: '' })}
                  />
                </div>
              )}
              <div>
                <Label>Role *</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin — full access</SelectItem>
                    <SelectItem value="employee">Employee — engineers & field staff</SelectItem>
                    <SelectItem value="subcontractor">Subcontractor — only their assigned work orders</SelectItem>
                    <SelectItem value="customer">Customer — portal access only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Email {form.role === 'customer' ? <span className="text-slate-400 font-normal">(optional for customers)</span> : '*'}</Label>
              <Input data-testid="user-email-input" type="email" className="mt-1.5" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="sarah@lutrom.co.uk" />
            </div>
            <div>
              <Label>{editing ? 'New password (leave blank to keep current)' : 'Password (optional — invite email lets them set their own)'}</Label>
              <Input data-testid="user-password-input" type="text" className="mt-1.5" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={editing ? '••••••' : 'Leave blank to send invite link'} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Phone</Label>
                <Input className="mt-1.5" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+44 7700 900123" />
              </div>
              <div>
                <Label>Title</Label>
                <Input className="mt-1.5" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Senior Engineer" />
              </div>
            </div>
            {form.role === 'customer' && form.xeroContactId && (
              <div className="text-xs text-green-700 flex items-center gap-1">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500" />
                Linked to Xero contact · invoices will auto-sync to this company
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="save-user-btn">
              {saving ? 'Saving...' : editing ? 'Save changes' : 'Create user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick "Link to Xero" dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-lg" data-testid="link-xero-dialog">
          <DialogHeader>
            <DialogTitle>Link {linkUser?.name} to a Xero contact</DialogTitle>
            <DialogDescription>
              Pick a matching contact from Xero. Invoices for this customer will auto-sync to the selected company.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              data-testid="link-xero-search"
              placeholder={loadingContacts ? 'Loading contacts from Xero...' : 'Search by company or email'}
              value={linkQuery}
              onChange={(e) => setLinkQuery(e.target.value)}
              autoFocus
            />
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded bg-white">
              {loadingContacts && (
                <div className="p-4 text-sm text-slate-500">Loading...</div>
              )}
              {!loadingContacts && xeroContacts
                .filter(c => {
                  if (!linkQuery.trim()) return true;
                  const q = linkQuery.toLowerCase();
                  return (c.name || '').toLowerCase().includes(q)
                      || (c.email || '').toLowerCase().includes(q);
                })
                .slice(0, 50)
                .map(c => (
                  <button
                    key={c.xeroContactId}
                    type="button"
                    disabled={linkSaving}
                    onClick={() => saveLink(c.xeroContactId)}
                    data-testid={`link-xero-contact-${c.xeroContactId}`}
                    className={`w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 text-sm ${
                      linkUser?.xeroContactId === c.xeroContactId ? 'bg-emerald-50' : ''
                    }`}
                  >
                    <div className="font-medium text-slate-900 truncate">{c.name}</div>
                    {c.email && <div className="text-xs text-slate-500 truncate">{c.email}</div>}
                    {linkUser?.xeroContactId === c.xeroContactId && (
                      <div className="text-xs text-emerald-700 mt-0.5">Currently linked</div>
                    )}
                  </button>
                ))}
              {!loadingContacts && xeroContacts.length === 0 && (
                <div className="p-4 text-sm text-slate-500">
                  No Xero contacts found. Connect to Xero on the Xero Settings page.
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="justify-between">
            <Button
              variant="outline"
              disabled={linkSaving || !linkUser?.xeroContactId}
              onClick={() => saveLink('')}
              data-testid="link-xero-unlink-btn"
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              Unlink
            </Button>
            <Button variant="outline" onClick={() => setLinkOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;

// ------- Xero Contact Picker (search + dropdown) -------
const XeroContactPicker = ({ xeroConnected, loading, contacts, value, xeroContactId, onSelect, onTypeCustom }) => {
  const [query, setQuery] = useState(value || '');
  const [focused, setFocused] = useState(false);

  useEffect(() => { setQuery(value || ''); }, [value]);

  if (!xeroConnected) {
    return (
      <Input
        className="mt-1.5"
        data-testid="user-company-input"
        value={query}
        onChange={e => { setQuery(e.target.value); onTypeCustom(e.target.value); }}
        placeholder="Acme Manufacturing Ltd"
      />
    );
  }

  const q = query.trim().toLowerCase();
  const matches = q
    ? contacts.filter(c => c.name.toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q)).slice(0, 10)
    : contacts.slice(0, 10);

  const selected = xeroContactId ? contacts.find(c => c.xeroContactId === xeroContactId) : null;

  return (
    <div className="relative mt-1.5">
      <Input
        value={query}
        onChange={e => { setQuery(e.target.value); onTypeCustom(e.target.value); }}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 180)}
        placeholder={loading ? 'Loading Xero contacts...' : `Search ${contacts.length} Xero contacts or type to create`}
        disabled={loading}
        data-testid="xero-contact-picker-input"
      />
      {focused && !loading && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
          {matches.length === 0 && (
            <div className="px-3 py-4 text-sm text-slate-500">
              No matching Xero contact. The name you typed will be saved as a plain company name (not linked to Xero).
            </div>
          )}
          {matches.map(c => (
            <button
              key={c.xeroContactId}
              type="button"
              onClick={() => { setQuery(c.name); onSelect(c); setFocused(false); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
              data-testid={`xero-contact-option-${c.xeroContactId}`}
            >
              <div className="text-sm font-medium text-slate-900">{c.name}</div>
              {c.email && <div className="text-xs text-slate-500 truncate">{c.email}</div>}
            </button>
          ))}
          {selected && matches.find(m => m.xeroContactId === selected.xeroContactId) == null && (
            <div className="px-3 py-2 bg-blue-50 text-xs text-blue-900 border-t border-blue-200">
              Currently linked: <strong>{selected.name}</strong>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
